<template>
  <v-container
    class="products-list-page"
    fluid>
    <v-row>
      <v-col cols="3">
        <v-row class="products-row">
          <v-col cols="12">
            <side-bar
              :products="products"
              :fetching-product="gettingProducts"
              @input:offset="setOffset($event)"
              @add-product="addProductToSelected($event)"
              @search="getProducts($event)" />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="9">
        <v-row class="products-row">
          <v-col cols="12">
            <qr-data-table
              :is-show-fit="isShowFit"
              :products="selectedProduct"
              :offset="offset"
              @remove-by-index="removeSelected($event)" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { generateQRCode } from '@/assets/js/helper'
import ProductProvider from '@/resources/ProductProvider'
import ConsumerTypes from '@/assets/js/ConsumerTypes'
import SideBar from '../components/sideBar.vue'
import qrDataTable from '../components/qrDataTable.vue'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'
import ExportPriceConversionsProvider from '@/resources/ExportPriceConversionsProvider'
import { mapProductExportCurrencyPrice, showPriceLocale } from '@/assets/js/Currencies'
import PrototypeProvider from '@/resources/PrototypeProvider'

const ProductService = new ProductProvider()
const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()
const ExportPriceConversionsService = new ExportPriceConversionsProvider()
const PrototypeService = new PrototypeProvider()

export default {
  components: {
    SideBar,
    qrDataTable
  },
  data () {
    return {
      gettingProducts: false,
      selectedProduct: [],
      products: [],
      offset: 0,
      webUrl: {
        GW: 'https://www.gentlewomanonline.com',
        LW: 'https://www.gentlewomanonline.com',
        GU: 'https://www.gentlewomanonline.com',
        MM: 'https://matter-makers.com'
      },
      currenciesItems: [],
      conversionPrices: [],
      sortCurrencies: [
        'IDR',
        'MYR',
        'SGD'
      ]
    }
  },
  computed: {
    ...mapGetters({
      store: 'Store/store'
    }),
    isShowFit () {
      return this.store?.id === '645a1d00765d5519beeb97f6'
    }
  },
  async mounted () {
    await this.initMasterData()
    await this.getProducts()
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage',
      setSnackbar: 'Components/setSnackbar'
    }),
    async getProducts (search = '') {
      try {
        this.gettingProducts = true
        const { data } = await ProductService.getProducts({ search })
        this.products = this.mappingSkus(data.results)
      } catch (error) {
        console.error('getting product', error)
        this.setErrorPage(error.code)
      } finally {
        this.gettingProducts = false
      }
    },
    setOffset (value) {
      this.offset = value
    },
    mappingSkus (products) {
      const allSkus = []
      products.forEach((product) => {
        const tempSkus = product.skus.map((sku) => ({
          ...sku,
          name: product.name,
          model: product.model,
          collection: product.gwCollection,
          prototypeCategory: product.productPrototype?.category || 'UNKNOWN',
          prototypeId: product.productPrototype?.id || null,
          tags: product.tags
        }))

        allSkus.push(...tempSkus)
      })

      return allSkus.map((sku) => {
        const currencies = this.currenciesItems.map((c) => mapProductExportCurrencyPrice(sku, c, 10, this.conversionPrices))
        const merCode = sku.name.split('-')
        const productName = merCode[merCode.length - 1]
        const options = this.initOptions(sku.size, sku.color, sku.tags)
        const fitting = this.getFitting(sku.tags)
        const thPrice = showPriceLocale(sku.price, 'TH', 'THB')
        const currency = {
          price: thPrice.price,
          priceUnit: thPrice.currency,
          priceSymbol: '฿'
        }
        const sortedCurrencies = currencies
          .filter((c) => this.sortCurrencies.includes(c.currency))
          .sort((a, b) => this.sortCurrencies.indexOf(a.currency) - this.sortCurrencies.indexOf(b.currency))
        sortedCurrencies.forEach((c, index) => {
          const convertPrice = showPriceLocale(c.price, c.locale, c.currency)
          currency[`price${index + 1}`] = convertPrice.price
          currency[`price${index + 1}Unit`] = c.currency
          currency[`price${index + 1}Symbol`] = convertPrice.currency
        })
        return {
          skuCode: sku.code,
          productName,
          gwCollection: sku.collection?.nickname ? sku.collection?.nickname : 'COLLECTION : -',
          prototypeCategory: sku.prototypeCategory,
          prototypeId: sku.prototypeId,
          options,
          images: sku.images,
          fitting,
          ...currency,
          totalCurrency: sortedCurrencies.length
        }
      })
    },
    getFitting (tags) {
      if (this.isShowFit) {
        if (tags && tags.length) {
          const fittingTag = tags.find((tag) => ['UNISEX', 'WOMEN', 'KIDS', 'MEN'].includes(tag.toUpperCase()))
          if (fittingTag) {
            return `${fittingTag.toUpperCase()}`
          }
        }
        return 'FIT : -'
      }
      return ''
    },
    initOptions (size, color) {
      let option = ''
      if (size.name && color.name) {
        option = `${color.name.toUpperCase()} - ${size.name.toUpperCase()}`
      } else if (size.name && !color.name) {
        option = size.name.toUpperCase()
      } else if (!size.name && color.name) {
        option = color.name.toUpperCase()
      }

      return option
    },
    async addProductToSelected (_product) {
      let manufacCountry = ''
      if (_product?.prototypeId) {
        const { data: prototypeInfo } = await PrototypeService.getPrototypeByIdWithoutCost(_product.prototypeId)
        if (prototypeInfo?.order?.manufacCountry) {
          manufacCountry = prototypeInfo?.order?.manufacCountry === 'malaysia' ? 'china' : prototypeInfo?.order?.manufacCountry
        }
      }
      if (this.isShowFit && _product.fitting === 'FIT : -') {
        this.setSnackbar({
          value: true,
          message: 'รายการสินค้านี้ไม่ได้ระบุ Fit กรุณาตรวจสอบ',
          type: 'error',
          timeout: 6000
        })
      }
      const subBrandCode = _product.skuCode.split('.')?.[0]?.toUpperCase()
      const url = `${this.webUrl[subBrandCode]}/${_product.skuCode}`
      const qr = await generateQRCode(url)
      const consumerTypeInfo = ConsumerTypes(_product.prototypeCategory.toLowerCase())
      const product = {
        ..._product,
        qr,
        productionDate: this.$dayjs().subtract(2, 'month').format('MM-YYYY'),
        fitting: _product.fitting,
        consumerType: `${consumerTypeInfo.en.toUpperCase()} / ${consumerTypeInfo.th}`,
        manufacCountry
      }
      const duplicated = this.selectedProduct.findIndex((selected) => selected.skuCode === product.skuCode)
      if (duplicated === -1) {
        this.selectedProduct.push({ ...product, amount: 1 })
      } else {
        this.selectedProduct[duplicated].amount++
      }
    },
    removeSelected (index) {
      this.selectedProduct.splice(index, 1)
    },
    async initMasterData () {
      const [currenciesItems, conversionPrice] = await Promise.all([
        ExportCurrencyRatesService.getItems(this.query),
        ExportPriceConversionsService.getAllActiveItems(this.query)
      ])
      if (currenciesItems?.data) this.currenciesItems = currenciesItems?.data.filter((cur) => cur.displayCountryPriceInTag) || []
      if (conversionPrice?.data) this.conversionPrices = conversionPrice.data || []
    }
  }
}
</script>
